import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type DS from 'ember-data';
import type {
  GetCompanyQuery,
  GetRoomsLocationWithConfigsQuery,
  GetRoomsQuery,
  GetSpaceSaverConfigQuery,
} from 'garaje/graphql/generated/roomba-types';
import type PluginModel from 'garaje/models/plugin';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type RoombaGraphqlService from 'garaje/services/roomba-graphql';
import type StateService from 'garaje/services/state';
import type { ResourceTypeConfig } from 'garaje/types/generic-resources/ResourceTypeConfig';
import urlBuilder from 'garaje/utils/url-builder';

export interface ResourcesResourceTypeIndexRouteModel {
  resourceTypeLabel: string;
  resourceTypeId: string;
  [x: string]: unknown;
}

interface RoomSettingsModel {
  company: GetCompanyQuery['company'];
  roomLocationConfig: GetRoomsLocationWithConfigsQuery['location'];
  currentLocation: StateService['currentLocation'];
  spaceSaverConfig: GetSpaceSaverConfigQuery['spaceSaverConfig'];
  plugins: DS.RecordArray<PluginModel>;
  rooms: GetRoomsQuery['rooms'];
}

export default class ResourcesResourceTypeIndexRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;
  @service declare router: RouterService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare roombaGraphql: RoombaGraphqlService;
  @service declare ajax: AjaxFetchService;

  beforeModel(): void {
    const resourceTypeId = this.currentResourceTypeId;

    if (resourceTypeId === '1' && this.abilities.cannot('visit-settings desks')) {
      this.router.transitionTo('unauthorized');
    }
  }

  async model(): Promise<ResourcesResourceTypeIndexRouteModel> {
    const resourceTypeId = this.currentResourceTypeId;

    let resourceTypeLabel = 'Resource';
    let model: { [x: string]: unknown } = {};

    if (resourceTypeId === '1') {
      resourceTypeLabel = 'Desk';
      await this.loadDeskSettings();
    }

    if (resourceTypeId === '2') {
      resourceTypeLabel = 'Parking';
      model = (await this.loadResourceSettings(resourceTypeId)) as unknown as { [x: string]: unknown };
    }

    if (resourceTypeId === '3') {
      resourceTypeLabel = 'Room';
      model = (await this.loadRoomSettings()) as unknown as { [x: string]: unknown };
    }

    return {
      resourceTypeLabel,
      resourceTypeId,
      ...model,
    };
  }

  get currentResourceTypeId(): string {
    const { resource_type_id } = this.paramsFor('resources.resource-type') as {
      resource_type_id: string;
    };

    return resource_type_id;
  }

  async loadDeskSettings(): Promise<void> {
    const currentLocationEnabledDesks = this.state.currentLocationEnabledDesks;
    const canAccessDesksApplication = !!this.state.features?.canAccessDesksApplication;

    if (
      (!this.featureConfig.isEnabled('desks') && !canAccessDesksApplication) ||
      !currentLocationEnabledDesks?.active
    ) {
      return void this.router.transitionTo('resources.resource-type.landing', 1);
    }

    await this.store.findRecord('desk-location', currentLocationEnabledDesks.id, {
      reload: true,
      include: 'workplace-days',
    });
  }

  async loadResourceSettings(resourceTypeId: string): Promise<{ resourceTypeConfig: ResourceTypeConfig }> {
    const url = urlBuilder.rms.resourceType(Number(resourceTypeId), this.state.currentLocation.id);
    const response: { data: ResourceTypeConfig } = await this.ajax.request(url);

    return {
      resourceTypeConfig: response.data,
    };
  }

  async loadRoomSettings(): Promise<RoomSettingsModel> {
    const { currentLocation } = this.state;

    if (!currentLocation) {
      throw new Error('no current location found while loading roomba settings');
    }

    const [company, roomLocationConfig, spaceSaverConfig, rooms, plugins] = await Promise.all([
      this.roombaGraphql.getCompany(),
      this.roombaGraphql.getRoomsLocationWithConfigs(currentLocation.id),
      this.roombaGraphql.getSpaceSaverConfig(),
      this.roombaGraphql.getRooms(this.state.currentLocation?.id),
      this.store.query('plugin', {
        include: 'plugin',
        filter: {
          notification_channels: true,
        },
        page: {
          limit: 100,
        },
      }),
    ]);

    return {
      company,
      roomLocationConfig,
      spaceSaverConfig,
      rooms,
      plugins,
      currentLocation: this.state.currentLocation,
    };
  }
}
